import Box from "@mui/material/Box";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import { Avatar, Typography } from "@mui/material";
import { Link, useNavigate, useParams } from "react-router-dom";
import { colors } from "../utils/constants";
import { useEffect, useState } from "react";
import { getEvents } from "../api/events";
import { Event } from "../interfaces/Event";
import { formatDatePtBr } from "../utils/dateUtils";
import ArowIcon from "@mui/icons-material/ArrowRightAlt";
import SearchIcon from "@mui/icons-material/Search";
import { toast } from "react-toastify";
import "./index.css";
import moment from "moment-timezone";
import Loading from "../components/Loading";
import { getClientById } from "../api/clients";
import Row from "../components/Row";
import ButtonLoading from "../components/ButtonLoading";
import { Opacity } from "@mui/icons-material";

export default function Events() {
  const navigate = useNavigate();
  const { client_id } = useParams();
  const [events, setEvents] = useState<Event[]>([]);
  const [client, setClient] = useState<any>([]);
  const [loadingEvents, setLoadingEvents] = useState(true);
  const [loadingClient, setLoadingClient] = useState(true);
  const loading = loadingEvents || loadingClient;

  useEffect(() => {
    fetchClients();
    fetchEvents();
  }, []);

  const fetchEvents = () => {
    getEvents(client_id)
      .then((response: Event[]) => {
        setEvents(response);
      })
      .catch((err) => {
        console.log(err);
      })
      .finally(() => {
        setLoadingEvents(false);
      });
  };

  const fetchClients = () => {
    getClientById(client_id)
      .then((response: any) => {
        setClient(response);
      })
      .catch((err) => {
        console.log(err);
      })
      .finally(() => {
        setLoadingClient(false);
      });
  };

  const goToEventDetails = (inProgress: boolean, id: String) => {
    navigate(`/${client_id}/${id}`);
  };

  const renderEvents = (event: Event) => {
    const today = moment.tz("America/Sao_Paulo").startOf("day");
    const dateInitEvent = moment(event.dateInit)
      .tz("America/Sao_Paulo")
      .startOf("day");
    const dateFinalEvent = moment(event.dateFinal)
      .tz("America/Sao_Paulo")
      .startOf("day");

    const inProgress =
      (today.isAfter(dateInitEvent) && today.isBefore(dateFinalEvent)) ||
      today.isSame(dateInitEvent) ||
      today.isSame(dateFinalEvent);

    const isPast = today.isAfter(dateFinalEvent);
    const isFuture = today.isBefore(dateInitEvent);

    const statusLabel = inProgress
      ? "Em andamento"
      : isFuture
      ? "Aguardando evento"
      : "Evento encerrado";

    const statusColor = inProgress
      ? colors.status.green
      : isFuture
      ? colors.status.yellow
      : colors.status.red;

    return (
      <ListItem
        key={event.id}
        style={{ ...styles.cardEvent }}
        className="card-event dashboard-card"
        onClick={() => goToEventDetails(inProgress || isPast, event.id)}
      >
        <Box style={styles.content}>
          <Box style={styles.bannerWrap}>
            <img
              style={styles.bannerEvent as React.CSSProperties}
              src={event.banner}
              alt={event.title}
              loading="lazy"
              decoding="async"
            />
          </Box>

          <Box style={styles.infoContent}>
            <Box style={styles.titleContainer} className="title-container">
              <span style={styles.eventTitle}>{event.title}</span>

              <Box
                component="span"
                sx={{ ...styles.badge, backgroundColor: statusColor }}
              >
                {statusLabel}
              </Box>
            </Box>

            <span style={styles.eventSubtitle}>{event.subtitle}</span>

            <Box style={styles.cardFooter}>
              <span style={styles.eventDate}>
                De {formatDatePtBr(new Date(event.dateInit))} até{" "}
                {formatDatePtBr(new Date(event.dateFinal))}
              </span>
              <ArowIcon style={styles.arrow} />
            </Box>
          </Box>
        </Box>
      </ListItem>
    );
  };

  const today = moment.tz("America/Sao_Paulo").startOf("day");

  const activeEvents = events.filter((event) => {
    const dateFinal = moment(event.dateFinal)
      .tz("America/Sao_Paulo")
      .startOf("day");
    return today.isBefore(dateFinal) || today.isSame(dateFinal);
  });

  const pastEvents = events.filter((event) => {
    const dateFinal = moment(event.dateFinal)
      .tz("America/Sao_Paulo")
      .startOf("day");
    return today.isAfter(dateFinal);
  });

  return (
    <Box sx={styles.container} className="event-container">
      {isMobile && (
        <Row style={{ paddingTop: 24, paddingBottom: 12 }}>
          <SearchIcon />
          <Link to="/events">Pesquisar eventos</Link>
        </Row>
      )}
      <Row style={styles.headerContainer}>
        <Avatar alt="User Avatar" src={client.logo} sx={styles.logo} />
        <Typography sx={styles.eventsTitle}>{client.client_name}</Typography>
      </Row>

      {loading && <Loading />}

      {!loading && events && (
        <>
          {activeEvents.length > 0 && (
            <>
              <Typography sx={styles.sectionTitle}>Eventos Ativos</Typography>
              <List style={styles.eventsListContainer}>
                {activeEvents.map((event) => renderEvents(event))}
              </List>
            </>
          )}

          {pastEvents.length > 0 && (
            <>
              <Typography sx={styles.sectionTitle}>
                Eventos Vencidos
              </Typography>
              <List style={styles.eventsListContainer} sx={{ opacity: 0.5 }}>
                {pastEvents.map((event) => renderEvents(event))}
              </List>
            </>
          )}
        </>
      )}

      {!loading && events.length === 0 && (
        <Box sx={styles.noEventsContainer}>
          <span style={styles.noEventsText}>
            Nenhum evento cadastrado para empresa!
          </span>
          <ButtonLoading
            label="Voltar"
            isDelete
            onClick={() => navigate("/events")}
          />
        </Box>
      )}
    </Box>
  );
}

const isMobile = window.innerWidth < 768;

const styles: any = {
  container: {
    paddingTop: 10,
    marginLeft: isMobile ? 10 : 5,
    paddingLeft: isMobile ? 2 : 0,
    paddingRight: isMobile ? 2 : 0,
    width: "100%",
    display: "flex",
    flexDirection: "column",
  },

  headerContainer: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    marginTop: 24,
  },

  logo: {
    width: "56px",
    height: "56px",
    border: "2px solid #E5E9F0",
  },

  eventsTitle: {
    fontSize: isMobile ? "20px" : "26px",
    fontWeight: 700,
    color: "#14213D",
    marginLeft: "16px",
    letterSpacing: "-0.01em",
  },

  sectionTitle: {
    fontSize: isMobile ? "18px" : "20px",
    fontWeight: 700,
    color: "#14213D",
    marginTop: "40px",
    marginBottom: "16px",
    display: "block",
  },

  content: {
    display: "flex",
    flexDirection: "column",
    flexWrap: "wrap",
  },

  eventsListContainer: {
    marginTop: 4,
    display: "flex",
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 24,
  },

  bannerWrap: {
    width: "100%",
    aspectRatio: "16 / 9",
    backgroundColor: "#EEF1F5",
    overflow: "hidden",
  },

  bannerEvent: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
    display: "block",
  },

  cardEvent: {
    margin: 0,
    backgroundColor: colors.white,
    border: "1.5px solid #E5E9F0",
    maxWidth: 380,
    width: "100%",
    padding: 0,
    borderRadius: 16,
    overflow: "hidden",
    cursor: "pointer",
    flexDirection: "column",
    boxShadow: "0 10px 24px -14px rgba(20,33,61,.20)",
  },

  badge: {
    padding: "6px 14px",
    color: colors.white,
    borderRadius: "100px",
    fontSize: "11px",
    fontWeight: 700,
    letterSpacing: "0.02em",
    textTransform: "uppercase" as const,
    whiteSpace: "nowrap" as const,
    flexShrink: 0,
    marginLeft: "12px",
  },

  infoContent: {
    display: "flex",
    flexDirection: "column",
    padding: 20,
    width: "100%",
  },

  titleContainer: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    width: "100%",
    alignItems: "flex-start",
  },

  eventTitle: {
    fontSize: 17,
    fontWeight: 700,
    color: "#14213D",
    lineHeight: 1.3,
  },

  eventSubtitle: {
    fontSize: 14,
    fontWeight: 400,
    color: "#5B6472",
    whiteSpace: "pre-line",
    width: "100%",
    minHeight: 42,
    marginTop: 12,
    lineHeight: 1.5,
    display: "-webkit-box",
    WebkitLineClamp: 3,
    WebkitBoxOrient: "vertical",
    overflow: "hidden",
  },

  cardFooter: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginTop: 16,
    paddingTop: 16,
    borderTop: "1px solid #EEF1F5",
  },

  eventDate: {
    fontSize: 13,
    fontWeight: 600,
    color: "#5B6472",
  },

  arrow: {
    color: colors.primary,
    fontSize: 22,
  },

  noEventsContainer: {
    display: "flex",
    flexDirection: "column",
    flex: 1,
    alignItems: "center",
    alignSelf: "center",
    justifyContent: "center",
    width: isMobile ? "90%" : "50%",
    minHeight: "240px",
    backgroundColor: "#F7F9FC",
    border: "1.5px dashed #E5E9F0",
    borderRadius: "16px",
    gap: "16px",
    padding: "32px",
  },

  noEventsText: {
    fontSize: 15,
    fontWeight: 500,
    color: "#5B6472",
    textAlign: "center" as const,
  },
};
